import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { Genome } from 'src/providers/genome/genome';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { RestockOrderPage } from 'src/pages/inventory/restock-order/restock-order.page';
import {
  RestockingOrdersService,
  RestockCartLine,
  RestockCartDraft,
  BrandCandidate,
  RestockOrderError,
  isRestockCartLineReady
} from 'src/shared/services/restocking-orders.service';

import { packLabel } from 'src/models/restocking-brand.model';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

interface CartLineVM extends RestockCartLine {
  showBackups: boolean;
  headerHelper: string;      // 'Current 2 · Min 6 · Target 24'
  qtyText: string;           // '2 packs (24 items)'
  firstChoiceText: string;   // '1st choice: Charmin Ultra Soft Mega, 12 rolls'
  backups: { text: string }[];
  // An item can't be restocked without a preferred brand (what to buy), a target
  // quantity (how much to top up to) and a max price (the concierge's ceiling).
  // Missing any of them replaces the brand row with a prompt to finish setup.
  hasBrands: boolean;
  needsSetup: boolean;
  setupText: string;         // 'Add a preferred brand, target quantity and max price to restock this item'
  goToItem: () => void;
}

// Distinguishes "not configured" (null) from a real 0 on a room-object level.
function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function ordinal(rank: number): string {
  switch (rank) {
    case 1: return '1st';
    case 2: return '2nd';
    case 3: return '3rd';
    default: return `${rank}th`;
  }
}

// Low stock = current below min; Below Opportunistic = current below the
// opportunistic top-up level (but at/above min); everything else can be added.
function classifyLevel(current: number, min: number, opportunistic: number): 'low' | 'opportunistic' | 'ok' {
  if (min > 0 && current < min) {
    return 'low';
  }
  if (opportunistic > 0 && current < opportunistic) {
    return 'opportunistic';
  }
  return 'ok';
}

@Component({
  selector: 'order-restock-page',
  templateUrl: 'order-restock.page.html',
  standalone: true,
  imports: [CommonModule, IonicModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OrderRestockPage implements OnInit {
  private readonly client = inject(Client);
  private readonly genome = inject(Genome);
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly restockingService = inject(RestockingOrdersService);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly windowService = inject(WindowService);

  loaded = false;
  itemsLoading = false;
  readonly skeletonRows = [0, 1, 2, 3];
  isRightSideContent = false;
  dialogParams: any;

  propertyId: number;
  propertyName = '';
  editOrderId: number | null = null;

  lowLines: CartLineVM[] = [];
  opportunisticLines: CartLineVM[] = [];
  addedLines: CartLineVM[] = [];
  moreLines: CartLineVM[] = [];
  filteredMoreLines: CartLineVM[] = [];
  visibleMoreLines: CartLineVM[] = [];
  hasMoreToShow = false;
  showAllMore = false;

  propertyForm = new FormGroup({
    property: new FormControl<number | null>(null)
  });
  propertyItems: { value: number; viewValue: string }[] = [];
  submitButtonText = 'Add to Concierge Review';

  searchForm = new FormGroup({ search: new FormControl('') });

  // Real consumable room objects for the property, classified by stock level.
  private allPropertyLines: RestockCartLine[] = [];
  private pendingDraft: RestockCartDraft | null = null;

  boundSubmit = this.submit.bind(this);

  get submitDisabled(): boolean {
    const lines = this.selectedCartLines();
    return lines.length === 0 || lines.some((line) => !isRestockCartLineReady(line));
  }

  async ngOnInit() {
    this.pendingDraft = this.restockingService.takeCartDraft();
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
      this.rightSidePanelService.setDialogPageTitle('Create Order');
    }
    this.propertyId = this.navCtrl.getParam('propertyId') || this.dialogParams?.propertyId || this.pendingDraft?.propertyId;
    this.propertyName = this.navCtrl.getParam('propertyName') || this.dialogParams?.propertyName || this.pendingDraft?.propertyName || '';
    this.editOrderId = this.navCtrl.getParam('editOrderId') || this.dialogParams?.editOrderId || this.pendingDraft?.editOrderId || null;
    await this.loadProperties();
    this.syncPropertySelection();
    this.setupSearch();
    this.loaded = true;
    this.cdr.markForCheck();
    await this.reloadItems();
  }

  // Load (or reload on property switch) the property's items behind a skeleton.
  private async reloadItems() {
    this.itemsLoading = true;
    this.cdr.markForCheck();
    await this.loadPropertyRoomObjects();
    this.loadCart();
    this.itemsLoading = false;
    this.cdr.markForCheck();
  }

  private async loadProperties() {
    try {
      const addresses = await this.client.getAddresses();
      this.propertyItems = this.client.parseAddressList(addresses, false);
    } catch {
      this.propertyItems = [];
    }
    if (this.propertyItems.length === 0 && this.propertyName) {
      this.propertyItems = [{ value: this.propertyId ?? 0, viewValue: this.propertyName }];
    }
  }

  private syncPropertySelection() {
    const match = this.propertyItems.find((item) => item.value === this.propertyId) || this.propertyItems[0];
    if (!match) {
      return;
    }
    this.propertyId = match.value;
    this.propertyName = match.viewValue;
    this.propertyForm.get('property').setValue(match.value, { emitEvent: false });
  }

  async changeProperty() {
    const value = this.propertyForm.get('property').value;
    const match = this.propertyItems.find((item) => String(item.value) === String(value));
    if (!match) {
      return;
    }
    this.pendingDraft = null;
    this.restockingService.clearCartDraft();
    this.propertyId = Number(match.value);
    this.propertyName = match.viewValue;
    this.searchForm.get('search').setValue('', { emitEvent: false });
    this.showAllMore = false;
    await this.reloadItems();
  }

  // Load the property's real consumable room objects and classify each by level.
  private async loadPropertyRoomObjects() {
    this.allPropertyLines = [];
    if (!this.propertyId) {
      return;
    }
    try {
      const objects = await this.genome.getRoomsObjects(this.propertyId);
      const list = Array.isArray(objects) ? objects : [];
      this.allPropertyLines = list
        .filter((ro) => ro?.consumable)
        .map((ro) => this.roomObjectToLine(ro));
    } catch {
      this.allPropertyLines = [];
    }
  }

  private roomObjectToLine(ro: any): RestockCartLine {
    const brands = [...(ro?.preferred_brands || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
    const price = Number(ro?.min_restocking_price) || 0;
    // Keep unset levels as null rather than collapsing them to 0 — the cart shows
    // "?" for "never configured", which is what drives the setup prompt.
    const current = numOrNull(ro?.current_quantity);
    const min = numOrNull(ro?.min_quantity);
    const opportunistic = Number(ro?.opportunistic_quantity) || 0;
    // The client only knows the customer's ranked brand preferences and the price
    // they configured on the item. Per-brand SKUs, real prices and stock come from
    // the vendor at resolution time, so leave them unset — the UI renders those as
    // "price pending" rather than inventing a number.
    const candidates: BrandCandidate[] = brands.map((brand, index) => ({
      rank: index + 1,
      brand: brand.brand || '',
      productName: brand.product_name || '',
      sku: '',
      unitPrice: index === 0 ? price : 0,
      inStock: true,
      unitsPerPack: Number(brand.amount) > 0 ? Number(brand.amount) : undefined
    }));
    return {
      roomObjectId: Number(ro?.id),
      name: ro?.name || 'Item',
      icon: ro?.model?.category?.default_icon_url || 'assets/svg/cube-outline.svg',
      level: classifyLevel(current ?? 0, min ?? 0, opportunistic),
      currentQuantity: current,
      minQuantity: min,
      restockToQuantity: numOrNull(ro?.max_quantity),
      orderQuantity: 1,
      maxUnitPrice: Number(ro?.max_restocking_price) || price || 0,
      candidates
    };
  }

  private setupSearch() {
    this.searchForm.get('search').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateFilteredMore();
        this.cdr.markForCheck();
      });
  }

  private updateFilteredMore() {
    const query = (this.searchForm.get('search').value || '').trim().toLowerCase();
    this.filteredMoreLines = query
      ? this.moreLines.filter((line) => line.name.toLowerCase().includes(query))
      : this.moreLines;
    this.visibleMoreLines = this.showAllMore ? this.filteredMoreLines : this.filteredMoreLines.slice(0, 3);
    this.hasMoreToShow = !this.showAllMore && this.filteredMoreLines.length > 3;
  }

  viewMore() {
    this.showAllMore = true;
    this.updateFilteredMore();
    this.cdr.markForCheck();
  }

  private loadCart() {
    const draft = this.pendingDraft;
    this.pendingDraft = null;
    if (draft && draft.propertyId === this.propertyId && draft.editOrderId === this.editOrderId) {
      this.restoreCartDraft(draft);
      this.showAllMore = false;
      this.updateFilteredMore();
      return;
    }
    if (this.editOrderId) {
      this.submitButtonText = 'Update Your Order';
      this.loadEditCart(this.editOrderId);
    } else {
      this.submitButtonText = 'Add to Concierge Review';
      this.lowLines = this.linesForLevel('low');
      this.opportunisticLines = this.linesForLevel('opportunistic');
      this.addedLines = [];
      this.moreLines = this.linesForLevel('ok');
    }
    this.showAllMore = false;
    this.updateFilteredMore();
  }

  private restoreCartDraft(draft: RestockCartDraft) {
    const currentLines = new Map(this.allPropertyLines.map((line) => [line.roomObjectId, line]));
    const restoreLines = (savedLines: RestockCartLine[]): CartLineVM[] => savedLines
      .map((saved) => {
        const current = currentLines.get(saved.roomObjectId);
        if (!current) {
          return null;
        }
        return this.toCartVM({
          ...current,
          level: saved.level,
          orderQuantity: saved.orderQuantity
        });
      })
      .filter((line): line is CartLineVM => !!line);

    this.lowLines = restoreLines(draft.lowLines);
    this.opportunisticLines = restoreLines(draft.opportunisticLines);
    this.addedLines = restoreLines(draft.addedLines);
    this.moreLines = restoreLines(draft.moreLines);
  }

  private saveCartDraft() {
    this.restockingService.saveCartDraft({
      propertyId: this.propertyId,
      propertyName: this.propertyName,
      editOrderId: this.editOrderId,
      lowLines: this.lowLines.map((line) => this.toPlainLine(line)),
      opportunisticLines: this.opportunisticLines.map((line) => this.toPlainLine(line)),
      addedLines: this.addedLines.map((line) => this.toPlainLine(line)),
      moreLines: this.moreLines.map((line) => this.toPlainLine(line))
    });
  }

  private linesForLevel(level: 'low' | 'opportunistic' | 'ok'): CartLineVM[] {
    return this.allPropertyLines
      .filter((line) => line.level === level)
      .map((line) => this.toCartVM(line));
  }

  private loadEditCart(orderId: number) {
    const order = this.restockingService.getOrder(orderId);
    if (!order) {
      return;
    }
    this.propertyName = order.propertyName;
    this.propertyId = order.propertyId;
    this.lowLines = order.items.filter((item) => item.level === 'low').map((item) => this.toCartVM(item));
    this.opportunisticLines = order.items.filter((item) => item.level === 'opportunistic').map((item) => this.toCartVM(item));
    this.addedLines = order.items.filter((item) => item.level === 'ok').map((item) => this.toCartVM(item));
    const inCartIds = new Set(order.items.map((item) => item.roomObjectId));
    this.moreLines = this.allPropertyLines
      .filter((line) => line.level === 'ok' && !inCartIds.has(line.roomObjectId))
      .map((line) => this.toCartVM(line));
  }

  private toCartVM(line: RestockCartLine): CartLineVM {
    const vm: CartLineVM = {
      ...line,
      orderQuantity: this.normalizeQty(line.orderQuantity),
      qtyText: '',
      showBackups: false,
      headerHelper: '',
      firstChoiceText: '',
      backups: [],
      hasBrands: false,
      needsSetup: false,
      setupText: '',
      goToItem: () => undefined
    };
    vm.goToItem = () => this.goToItemSetup(vm);
    this.recomputeLine(vm);
    return vm;
  }

  // The backend can return null quantities for a room object; a cart line needs a
  // real, orderable count, so fall back to 1 (the user can then edit or remove it).
  private normalizeQty(value: number | null | undefined): number {
    const parsed = Math.floor(Number(value));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  private numOrQuestion(value: number | null | undefined): string {
    return value === null || value === undefined ? '?' : `${value}`;
  }

  private recomputeLine(vm: CartLineVM) {
    const ranked = [...vm.candidates].sort((a, b) => a.rank - b.rank);
    const first = ranked[0];
    // Packs are what gets ordered, but customers count units — show both when the
    // first-choice tier tells us how many units are in a pack.
    vm.qtyText = packLabel(vm.orderQuantity, this.unitsPerPackFor(vm));
    vm.headerHelper =`Current ${this.numOrQuestion(vm.currentQuantity)} · Min ${this.numOrQuestion(vm.minQuantity)} · Target ${this.numOrQuestion(vm.restockToQuantity)}`;
    vm.hasBrands = !!first;
    vm.setupText = this.setupPromptFor(vm.hasBrands, vm.restockToQuantity > 0, vm.maxUnitPrice > 0);
    vm.needsSetup = !!vm.setupText;
    vm.firstChoiceText = first
      ? `1st choice: ${first.brand} ${first.productName}${this.stockSuffix(first)}`
      : '';
    vm.backups = ranked.slice(1).map((candidate) => ({
      text: `${ordinal(candidate.rank)}: ${candidate.brand} ${candidate.productName}${this.stockSuffix(candidate)}`
    }));
  }

  // Units in one pack of the first choice. Lines rebuilt from an existing order come
  // back from the API without pack sizes (only the room-object waterfall has them),
  // so fall back to the property line we loaded for the same item.
  private unitsPerPackFor(vm: CartLineVM): number | null {
    const own = vm.candidates.find((candidate) => candidate.rank === 1)?.unitsPerPack;
    if (Number(own) > 0) {
      return Number(own);
    }
    const propertyLine = this.allPropertyLines.find((line) => line.roomObjectId === vm.roomObjectId);
    const fallback = propertyLine?.candidates.find((candidate) => candidate.rank === 1)?.unitsPerPack;
    return Number(fallback) > 0 ? Number(fallback) : null;
  }

  // An item can't be ordered without all three: what to buy, how much to top up
  // to, and the price ceiling the concierge must stay under. One prompt lists
  // whatever is still missing.
  private setupPromptFor(hasBrands: boolean, hasTargetQty: boolean, hasMaxPrice: boolean): string {
    const missing: string[] = [];
    if (!hasBrands) {
      missing.push('preferred brand');
    }
    if (!hasTargetQty) {
      missing.push('target quantity');
    }
    if (!hasMaxPrice) {
      missing.push('max price');
    }
    if (!missing.length) {
      return '';
    }
    const list = missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
    return `Add a ${list} to restock this item`;
  }

  // Vendor pricing doesn't exist until the concierge places the order, so a cart
  // line never shows a price — only a confirmed out-of-stock flag.
  private stockSuffix(candidate: { inStock: boolean }): string {
    return candidate.inStock === false ? ' (out of stock)' : '';
  }

  incrementQty(vm: CartLineVM) {
    vm.orderQuantity += 1;
    this.recomputeLine(vm);
    this.cdr.markForCheck();
  }

  decrementQty(vm: CartLineVM) {
    if (vm.orderQuantity <= 1) {
      return;
    }
    vm.orderQuantity -= 1;
    this.recomputeLine(vm);
    this.cdr.markForCheck();
  }

  toggleBackups(vm: CartLineVM) {
    if (vm.backups.length === 0) {
      return;
    }
    vm.showBackups = !vm.showBackups;
    this.cdr.markForCheck();
  }

  // "+" on an Add More card: pop it to the top of the cart as a full card.
  addLine(vm: CartLineVM) {
    this.moreLines = this.moreLines.filter((line) => line !== vm);
    this.addedLines = [vm, ...this.addedLines];
    this.updateFilteredMore();
    this.cdr.markForCheck();
  }

  removeLine(vm: CartLineVM) {
    if (vm.level === 'low') {
      this.lowLines = this.lowLines.filter((line) => line !== vm);
    } else if (vm.level === 'opportunistic') {
      this.opportunisticLines = this.opportunisticLines.filter((line) => line !== vm);
    } else {
      this.addedLines = this.addedLines.filter((line) => line !== vm);
      vm.showBackups = false;
      this.moreLines = [vm, ...this.moreLines];
      this.updateFilteredMore();
    }
    this.cdr.markForCheck();
  }

  private toPlainLine(vm: CartLineVM): RestockCartLine {
    return {
      roomObjectId: vm.roomObjectId,
      name: vm.name,
      icon: vm.icon,
      level: vm.level,
      currentQuantity: vm.currentQuantity,
      minQuantity: vm.minQuantity,
      restockToQuantity: vm.restockToQuantity,
      orderQuantity: vm.orderQuantity,
      maxUnitPrice: vm.maxUnitPrice,
      candidates: vm.candidates
    };
  }

  private selectedCartLines(): CartLineVM[] {
    return [...this.lowLines, ...this.opportunisticLines, ...this.addedLines];
  }

  async submit() {
    const selectedLines = this.selectedCartLines();
    if (selectedLines.some((line) => !isRestockCartLineReady(line))) {
      this.util.showError('Complete each item\'s preferred brand, target quantity and max price before submitting.');
      return;
    }
    // Drop zero-quantity lines: the backend rejects quantity <= 0, and "order 0 of
    // this" isn't a real request. A suggested line can arrive pre-set to 0 packs.
    const lines = selectedLines
      .map((vm) => this.toPlainLine(vm))
      .filter((line) => line.orderQuantity > 0);
    if (lines.length === 0) {
      this.util.showError('Add at least one item with a quantity to submit a preview cart.');
      return;
    }
    try {
      let orderId: number;
      if (this.editOrderId) {
        const order = await this.restockingService.updatePreviewCart(this.editOrderId, lines);
        orderId = order.id;
        this.util.showSuccess('Preview cart updated');
      } else {
        const order = await this.restockingService.submitPreview(this.propertyId, lines);
        orderId = order.id;
        this.util.showSuccess('Preview cart submitted');
      }
      this.restockingService.clearCartDraft();
      this.goToOrderDetail(orderId);
    } catch (error) {
      this.handleSubmitError(error);
    }
  }

  // The backend enforces one active cart per address, and requires a preferred-brand
  // link to order. Route or surface those instead of the generic retry toast.
  private handleSubmitError(error: unknown) {
    if (error instanceof RestockOrderError) {
      if (error.code === 'active_cart_exists' && error.existingOrderId) {
        this.util.showError('This property already has an open cart. Opening it to edit.');
        this.editExistingCart(error.existingOrderId);
        return;
      }
      if (error.message) {
        this.util.showError(error.message);
        return;
      }
    }
    this.util.showError('Unable to submit your preview cart. Please try again.');
  }

  // Open the edit form directly so the preferred-brand and restocking controls
  // are immediately available; the read-only item page otherwise adds an extra
  // navigation step before the customer can complete the requested setup.
  private async goToItemSetup(vm: CartLineVM) {
    this.saveCartDraft();
    try {
      const item = await this.genome.getRoomObject(vm.roomObjectId);
      if (!item) {
        throw new Error('Item not found');
      }
      const params = {
        item,
        addressId: item?.address_id || this.propertyId,
        showConsumables: true,
        cameFromRoute: 'order-restock'
      };
      localStorage.setItem('roomItemBackPage', 'order-restock');
      await this.rightSidePanelService.navigateTo('add-item-form', params);
    } catch (_error) {
      this.util.showError('Unable to open this item. Please try again.');
    }
  }

  private editExistingCart(orderId: number) {
    const params = { editOrderId: orderId, propertyId: this.propertyId, propertyName: this.propertyName };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('order-restock', params, OrderRestockPage);
    } else {
      this.navCtrl.navigateForward('order-restock', params);
    }
  }

  // hideBackButton: the cart is submitted at this point, so going "back" into
  // the builder would be misleading.
  private goToOrderDetail(orderId: number) {
    const params = { orderId, hideBackButton: true };
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo(`restock-order/${  orderId}`, params, RestockOrderPage);
    } else {
      this.navCtrl.navigateForward(`restock-order/${  orderId}`, params);
    }
  }
}
